import BooleanWithLabel from "@/Components/Boolean/BooleanWithLabel";
import Button from "@/Components/Button/Button";
import InputColor from "@/Components/Input/InputColor";
import Textfield from "@/Components/Input/Textfield";
import Modal, { ModalProps } from "@/Components/Modal/Modal";
import useMapsHook from "@/Components/Tables/useMapsHook";
import { useModalMeta, useSetModal } from "@/shared/atoms/useModal";
import useCommon from "@/shared/hooks/useCommon";
import useRestaurantMapMutation from "@/shared/mutations/restaurantMap/useRestaurantMapMutation";
import useRestaurantMapUpdate from "@/shared/mutations/restaurantMap/useRestaurantMapUpdate";
import useRestaurantMaps from "@/shared/queries/restaurantMap/useRestaurantMaps";
import { RestaurantMap } from "@/shared/utils/interfaces";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface OrderModalProps extends ModalProps {}

type FormValues = {
  name: string;
};

const MapModal = ({ onExit, ...props }: OrderModalProps) => {
  const { refetch, isPending: isFetchPending } = useRestaurantMaps();
  const create = useRestaurantMapMutation();
  const update = useRestaurantMapUpdate();
  const isPending = create.isPending;
  const [meta] = useModalMeta();
  const { item_id, name, is_active } = meta as RestaurantMap;

  const isUpdate = item_id !== undefined;

  const { add, destroy } = useMapsHook();
  const setModal = useSetModal();

  const { t } = useTranslation("", { keyPrefix: "mapModal" });
  const tCommon = useCommon();
  const { handleSubmit, control, register, errors } = useForm({
    defaultValues: isUpdate && {
      name,
    },
  });
  const onSubmit = (restaurantMap: RestaurantMap) => {
    if (isPending) return;
    if (isUpdate) {
      return update.mutateAsync({ ...restaurantMap, item_id }).then(() => {
        refetch().then(() => {
          setModal("");
        });
      });
    } else {
      return create.mutateAsync(restaurantMap).then(() => {
        refetch().then(() => {
          setModal("");
        });
      });
    }

    // update to endpoint
  };

  return (
    <Modal width={"50rem"} onExit={onExit} {...props}>
      <div className="flex items-center">
        <h2>{t(isUpdate ? "updateTitle" : "title")}</h2>
        <div className="ml-[2rem]">
          <Controller
            name={`background_color`}
            defaultValue={"#d79b83"}
            control={control}
            rules={{
              required: {
                value: true,
                message: tCommon("fieldRequired"),
              },
            }}
            render={({ onChange, value, name }) => (
              <div className="relative">
                <InputColor
                  hideTextfield
                  name={name}
                  errors={errors}
                  label={tCommon("backgroundColor")}
                  onChange={(value) => {
                    onChange(value);
                  }}
                  value={value}
                />
              </div>
            )}
          />
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name={`is_active`}
          defaultValue={is_active ?? true}
          control={control}
          rules={{
            required: false,
          }}
          render={(props) => (
            <div className="mb-[2rem]">
              <BooleanWithLabel
                onChange={(e) => {
                  return props.onChange(e.target.checked);
                }}
                checked={props.value}
              >
                {tCommon("active")}
              </BooleanWithLabel>
            </div>
          )}
        />
        <Textfield
          name="name"
          placeholder={t("placeholder")}
          error={errors?.name?.message}
          ref={register({
            required: {
              value: true,
              message: tCommon("fieldRequired"),
            },
          })}
          label={tCommon("name")}
        />
        <div className="mt-8 flex gap-8">
          <Button isPending={create.isPending || update.isPending}>
            {tCommon("save")}
          </Button>
          {isUpdate && (
            <Button
              isPending={isPending || isFetchPending}
              type="button"
              // onClick={() => destroy(id)}
              variant="danger"
            >
              {tCommon("delete")}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default MapModal;
